import React, { useState, useEffect, useMemo } from 'react'; // ✅ AGGIUNTO: useState, useEffect, useMemo
import { useNavigate, useLocation } from 'react-router-dom'; // ✅ AGGIUNTO: useLocation
import TableManager from '../components/data/TableManager';
import { logger } from '../utils/logger';
import DuplicateAttributesModal from '../components/modals/DuplicateAttributesModal';
import api from '../config/axios';
import { useToast } from '../hooks/useToast';

// Column Configuration - Aligned with backend tableProperties.js SKE_Oggetti but keeping existing fields that work
const columnConfig = [
  // --- Keys and Identifiers ---
  "OGG_Id_Oggetti=0,ID Oggetto",
  "OGG_IdRif_Oggetti=0,ID Rif. Oggetto,type=number",
  "OGG_Nome=2,Nome Oggetto,required",
  "OGG_Descrizione=2,Descrizione",
  "OGG_Id_Tabelle=2,Tabella Collegata,lookupName=tabelleLookup", // Mantengo lookupName per compatibilità
  "OGG_Namespace=2,Namespace",
  "OGG_Package=2,Package",
  "OGG_Id_Moduli=2,Modulo,required,lookupName=moduliLookup", // Mantengo lookupName per compatibilità
  "OGG_Prefisso=2,Prefisso,required,lookupName=prefissoLookup",
  "OGG_AcronimoCampi=1,Acronimo Campi",
  "OGG_Costruttore=1,Costruttore,type=boolean", // Aggiunto dal backend
  
  // --- Validity and Status (Hidden - managed by backend) ---
  "OGG_InizioValidita=0,Inizio Validità,type=datetime",
  "OGG_FineValidita=0,Fine Validità,type=datetime",
  "OGG_StatiRecord=0,Stato Record,required,lookupName=statiRecordLookup",
  
  // --- Audit Fields (Hidden - managed by backend) ---
  "OGG_UtenteInserimento=0,Utente Inserimento",
  "OGG_TerminaleInserimento=0,Terminale Inserimento",
  "OGG_DataOraInserimento=0,Data/Ora Inserimento,type=datetime",
  "OGG_UtenteModifica=0,Utente Modifica",
  "OGG_TerminaleModifica=0,Terminale Modifica",
  "OGG_DataOraModifica=0,Data/Ora Modifica,type=datetime",
  "OGG_Timestamp=0,Timestamp DB,type=datetime",
];

// Configuration for Lookups
const lookupConfig = {
  moduliLookup: {
    tableName: 'SKE_Moduli',
    valueField: 'MOD_Id_Moduli',
    labelField: 'MOD_CodiceModulo',
    additionalFields: ['MOD_DescrizioneModulo'],
  },
  tabelleLookup: {
    tableName: 'SKE_Tabelle',
    valueField: 'TAB_Id_Tabelle',
    labelField: 'TAB_Nome',
  },
  prefissoLookup: {
    staticOptions: [
      { value: 'Item', label: 'Item' },
      { value: 'Info', label: 'Info' }
    ]
  },
};

// Configuration for Filters
const filterConfig = [
  { field: 'OGG_Nome', label: 'Nome Oggetto', type: 'text' },
  { field: 'OGG_Descrizione', label: 'Descrizione', type: 'text' },
  { field: 'OGG_Id_Moduli', label: 'Modulo', type: 'lookup', lookupName: 'moduliLookup', lookupType: 'select' },
  { field: 'OGG_Id_Tabelle', label: 'Tabella', type: 'lookup', lookupName: 'tabelleLookup', lookupType: 'select' },
];

// Specific Input Types for Form Fields
const inputTypes = {
  OGG_Descrizione: 'textarea',
  OGG_IdRif_Oggetti: 'number',
  OGG_Id_Moduli: 'select',
  OGG_Id_Tabelle: 'select',
  OGG_Prefisso: 'select',
  OGG_InizioValidita: 'datetime-local',
  OGG_FineValidita: 'datetime-local',
  OGG_DataOraInserimento: 'datetime-local',
  OGG_DataOraModifica: 'datetime-local',
  OGG_Timestamp: 'datetime-local',
};

const OggettiPage = () => {
  const navigate = useNavigate();
  const location = useLocation(); // ✅ AGGIUNTO: useLocation hook
  const { showSuccess, showError } = useToast();
  
  // ✅ AGGIUNTO: State per gestire i filtri iniziali da navigation state
  const [initialFilters, setInitialFilters] = useState({});
  
  // ✅ NUOVO: State per gestire la duplicazione degli attributi
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateContext, setDuplicateContext] = useState(null);
  const [isProcessingDuplication, setIsProcessingDuplication] = useState(false);

  // ✅ AGGIUNTO: Estrai l'ID oggetto dallo stato di navigazione
  const oggettoIdFromState = useMemo(() => 
    location.state?.presetFilter?.OGG_Id_Oggetti, 
    [location.state]
  );

  // ✅ MODIFICATO: Effect per impostare i filtri iniziali (incluso supporto per new tab)
  useEffect(() => {
    const filters = {};
    
    // Check for filters from URL parameters (new tab navigation)
    const urlParams = new URLSearchParams(window.location.search);
    const filterKey = urlParams.get('filterKey');
    
    if (filterKey) {
      const filterData = sessionStorage.getItem(filterKey);
      if (filterData) {
        try {
          const parsedData = JSON.parse(filterData);
          if (parsedData.presetFilter?.OGG_Id_Oggetti) {
            filters.OGG_Id_Oggetti = parsedData.presetFilter.OGG_Id_Oggetti;
            logger.info('[OggettiPage] Received oggettoId from new tab navigation:', parsedData.presetFilter.OGG_Id_Oggetti);
          }
          // Clean up sessionStorage
          sessionStorage.removeItem(filterKey);
        } catch (error) {
          logger.error('[OggettiPage] Error parsing filter data from sessionStorage:', error);
        }
      }
    } else if (oggettoIdFromState) {
      // Existing logic for same-tab navigation
      filters.OGG_Id_Oggetti = oggettoIdFromState;
      logger.info('[OggettiPage] Received oggettoId for filtering OGG_Id_Oggetti:', oggettoIdFromState);
    } else {
      // Caso 3: Apertura dal menu - nessun filtro
      logger.info('[OggettiPage] Opened from menu - no preset filters');
    }

    setInitialFilters(filters);
  }, [oggettoIdFromState]);

  // ✅ AGGIUNTO: Calcola se fare ricerca automatica con filtri iniziali
  const shouldAutoSearchWithFilters = useMemo(() => {
    return Object.keys(initialFilters).length > 0;
  }, [initialFilters]);

  // ✅ NUOVO: Callback chiamato dopo la creazione di un oggetto
  const handleOggettoCreated = async (createdOggetto) => {
    logger.info('[OggettiPage] Oggetto creato:', createdOggetto);
    
    // Verifica se stiamo duplicando
    if (isDuplicating && duplicateContext?.sourceOggettoId) {
      logger.info('[OggettiPage] Duplicazione rilevata, avvio processo di duplicazione attributi');
      
      try {
        // Recupera gli attributi dell'oggetto originale
        const response = await api.get('/crud/SKE_Attributi', {
          params: {
            filters: JSON.stringify({ ATT_Id_Oggetti: duplicateContext.sourceOggettoId }),
            limit: 10000,
            offset: 0,
          },
        });
        
        const attributi = response.data?.records || response.data || [];
        logger.info(`[OggettiPage] Trovati ${attributi.length} attributi da duplicare`);
        
        // Aggiorna il contesto di duplicazione
        setDuplicateContext(prev => ({
          ...prev,
          targetOggettoId: createdOggetto.OGG_Id_Oggetti,
          targetOggettoName: createdOggetto.OGG_Nome,
          attributesCount: attributi.length,
          attributi: attributi,
        }));
        
        // Mostra la modale di conferma
        setDuplicateModalOpen(true);
        
      } catch (error) {
        logger.error('[OggettiPage] Errore nel recupero degli attributi:', error);
        showError('Errore nel recupero degli attributi da duplicare');
        setIsDuplicating(false);
        setDuplicateContext(null);
      }
    }
  };

  // ✅ NUOVO: Callback quando l'utente conferma/rifiuta la duplicazione degli attributi
  const handleDuplicateAttributesConfirm = async (shouldDuplicate) => {
    if (!shouldDuplicate) {
      logger.info('[OggettiPage] Utente ha rifiutato la duplicazione degli attributi');
      setDuplicateModalOpen(false);
      setIsDuplicating(false);
      setDuplicateContext(null);
      return;
    }
    
    logger.info('[OggettiPage] Utente ha confermato la duplicazione degli attributi');
    setIsProcessingDuplication(true);
    
    try {
      const { targetOggettoId, attributi = [] } = duplicateContext;
      
      if (attributi.length === 0) {
        logger.warn('[OggettiPage] Nessun attributo da duplicare');
        setDuplicateModalOpen(false);
        setIsDuplicating(false);
        setDuplicateContext(null);
        setIsProcessingDuplication(false);
        return;
      }
      
      // Prepara i cambiamenti in formato batch per il backend
      const changes = attributi.map((attr) => {
        // Rimuovi il primary key e i campi _text (lookup resolved)
        const { 
          ATT_Id_Attributi, 
          ATT_Id_Oggetti_text,
          ATT_TipiCampi_text,
          ATT_Super_Oggetti_text,
          ...attrWithoutPK 
        } = attr;
        
        return {
          operation: 'create',
          data: {
            ...attrWithoutPK,
            ATT_Id_Oggetti: targetOggettoId,
          }
        };
      });
      
      logger.info(`[OggettiPage] Duplicazione ${changes.length} attributi in batch`);
      
      // Esegui la chiamata batch
      const response = await api.post('/crud/SKE_Attributi', { changes });
      
      logger.info(`[OggettiPage] ${attributi.length} attributi duplicati con successo`, response.data);
      showSuccess(`${attributi.length} attributi duplicati con successo!`);
      
      setDuplicateModalOpen(false);
      setIsDuplicating(false);
      setDuplicateContext(null);
      
    } catch (error) {
      logger.error('[OggettiPage] Errore durante la duplicazione degli attributi:', error);
      showError('Errore durante la duplicazione degli attributi');
    } finally {
      setIsProcessingDuplication(false);
    }
  };

  // ✅ NUOVO: Funzione da passare a TableManager per tracciare quando inizia una duplicazione
  const handleDuplicateStart = (row) => {
    logger.info('[OggettiPage] Inizio duplicazione oggetto:', row);
    setIsDuplicating(true);
    setDuplicateContext({
      sourceOggettoId: row.OGG_Id_Oggetti,
      sourceOggettoName: row.OGG_Nome,
      targetOggettoId: null,
      targetOggettoName: null,
      attributesCount: 0,
      attributi: [],
    });
  };

  const handleNavigateToAttributi = (navInstance, oggettoId, moduloId) => {
    if (oggettoId !== undefined) {
      logger.info(`[OggettiPage] Navigating to Attributi page for OGG_Id_Oggetti: ${oggettoId}, OGG_Id_Moduli: ${moduloId}`);
      navInstance('/sisketron/attributi', { 
        state: { 
          initialFilter_ATT_Id_Oggetti: oggettoId,
          parentOggettoId: oggettoId,
          parentModuloId: moduloId
        } 
      });
    } else {
      logger.warn(`[OggettiPage] Missing oggettoId (${oggettoId}) for Attributi navigation.`);
    }
  };

  return (
    <>
      <TableManager
        tableName="SKE_Oggetti"
        primaryKey="OGG_Id_Oggetti"
        pageTitle="Gestione oggetti "
        columnConfig={columnConfig}
        filterConfig={filterConfig}
        lookupConfig={lookupConfig}
        inputTypes={inputTypes}
        enableCreate={true}
        enableEdit={true}
        enableDelete={true}
        autoSearchOnLoad={true}
        initialFilters={initialFilters}
        autoSearchOnInitialFilters={shouldAutoSearchWithFilters}
        onNavigateToRelated={handleNavigateToAttributi}
        relatedNavigationPathName="Attributi"
        onCreateSuccess={handleOggettoCreated}
        onDuplicateStart={handleDuplicateStart}
      />
      
      <DuplicateAttributesModal
        isOpen={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setIsDuplicating(false);
          setDuplicateContext(null);
        }}
        onConfirm={handleDuplicateAttributesConfirm}
        isProcessing={isProcessingDuplication}
        sourceOggettoName={duplicateContext?.sourceOggettoName || ''}
        targetOggettoName={duplicateContext?.targetOggettoName || ''}
        attributesCount={duplicateContext?.attributesCount || 0}
      />
    </>
  );
};

export default OggettiPage;